import React, {useContext, useRef, useEffect} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./TopicDescriptionCreation.css";
import {NavigationManagement} from "../../LandingPage";
import API from "../../../logic/API";
import {FileManager} from "@cubone/react-file-manager";
import "@cubone/react-file-manager/dist/style.css";
import {useState} from "react";
import "./FolderSection.css";
import {Button, ModalContext} from "@spg/component_library";
import {SnackbarContext} from "../../../reusables/SnackbarProvider";
import {KeycloakContext} from "../../utility/KeycloakProvider";
import {AppSettingsContext} from "../../utility/AppSettingsProvider";

/**
 * Controls the upload section when viewing topic descriptions with upload transfer type.
 */
export type File = {
  name: string;
  isDirectory: boolean;
  path: string;
  updatedAt: string;
  size: number;
};

function FolderSection(props: {
  alternative: "create" | "view" | "edit";
  navigationManagement: NavigationManagement;
  topicId: string;
  disabledReason?: "inactive" | "unauthorized";
}) {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [draftCommitId, setDraftCommitId] = useState<string>();
  const {keycloak} = useContext(KeycloakContext);
  const {fileStoreUrl} = useContext(AppSettingsContext);
  const [files, setFiles] = useState<File[]>([]);
  const addSnackbar = useContext(SnackbarContext).addSnackbar;
  const modalContext = useContext(ModalContext);
  const [fileAccessToken, setFileAccessToken] = useState("");
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const canUpload = isEditing && !!draftCommitId && !!fileAccessToken;

  const fileAccessTokenRenewTime = 5 * 60 * 1000; // 5 minutes
  const fileUploadUrl = canUpload
    ? `${fileStoreUrl}/folder_topics/${props.topicId}/drafts/${draftCommitId}/files`
    : undefined;
  const fileManagerPermissions = {
    create: false,
    rename: false,
    copy: false,
    move: false,
    delete: canUpload,
    upload: canUpload,
    download: false,
  };

  const fileUploadConfig = canUpload
    ? {
        url: fileUploadUrl,
        method: "PUT",
        headers: {
          "File-Access-Token": fileAccessToken,
          Authorization: "Bearer " + keycloak.token,
        },
      }
    : undefined;

  const mapToFileObject = (
    name: string,
    size: number,
    updated_at: string,
  ): File => ({
    name: name,
    isDirectory: false,
    path: `/${name}`,
    updatedAt: updated_at,
    size: size,
  });

  const loadFiles = async (isDraft: boolean) => {
    if (isDraft && draftCommitId) {
      const res = await API.getDraftFolderContent(props.topicId, draftCommitId);
      if (res.success && res.payload) {
        const manifest = res.payload.draft_manifest.files;
        const visible = manifest.filter((f) => f.state !== "removed");
        setFiles(
          visible.map((entry) =>
            mapToFileObject(entry.file_name, entry.size, entry.updated_at),
          ),
        );
      }
    } else {
      const res = await API.getTopicFolderContent(props.topicId);
      if (res.success && res.payload) {
        const initialNames = res.payload.file_list;
        setFiles(
          initialNames.map((entry) =>
            mapToFileObject(entry.name, entry.size, entry.updated_at),
          ),
        );
      }
    }
  };

  const startEditing = async () => {
    setIsEditing(true);
    await createDraftFolder();
  };

  const publishDraftFolder = async () => {
    setIsEditing(false);
    if (!draftCommitId) {
      return;
    }
    await API.publishDraftFolder(props.topicId, draftCommitId);
    setDraftCommitId(undefined);
    await loadFiles(false);
    addSnackbar({
      variant: "timeout",
      text: intl.formatMessage({
        id: "topicDescriptionCreation.multi_file_upload.snackbar.saved",
      }),
      timeout: 7000,
    });
  };

  const handleFileUploaded = async () => {
    loadFiles(true);
  };

  const handleFileDeleted = async (files: File[]) => {
    for (const file of files) {
      if (!draftCommitId) {
        return;
      }
      await API.deleteFileFromDraftFolder(
        props.topicId,
        draftCommitId,
        file.name,
      );
    }
    await loadFiles(true);
  };

  const createDraftFolder = async () => {
    const res = await API.createDraftFolder(props.topicId);
    if (res.success && res.payload) {
      setDraftCommitId(res.payload.commit_id);
    } else {
      console.error("Failed to create draft folder", res);
    }
  };

  const deleteDraftFolder = async () => {
    if (draftCommitId) {
      await API.deleteDraftFolder(props.topicId, draftCommitId);
    }
  };

  const getAccessToken = async () => {
    const tokenAbortController = new AbortController();
    API.getFileAccessToken(props.topicId, tokenAbortController).then(
      (response) => {
        if (response.success && response.payload) {
          setFileAccessToken(response.payload.token);
        }
      },
    );
  };

  useEffect(() => {
    if (props.navigationManagement.isTryingToNavigate.length === 0) return;
    if (!isEditing) props.navigationManagement.allowNavigation();
    else
      modalContext.showModal({
        idBase: "multi_file_upload",
        headline: intl.formatMessage({
          id: "topicDescriptionCreation.multi_file_upload.modal.leave.headline",
        }),
        body: intl.formatMessage({
          id: "topicDescriptionCreation.multi_file_upload.modal.leave.text",
        }),
        primaryButton: {
          name: intl.formatMessage({
            id: "topicDescriptionCreation.multi_file_upload.modal.leave.leave",
          }),
          onClick: () => {
            deleteDraftFolder();
            props.navigationManagement.allowNavigation();
          },
        },
        secondaryButton: {
          name: intl.formatMessage({
            id: "topicDescriptionCreation.multi_file_upload.modal.leave.cancel",
          }),
          onClick: props.navigationManagement.preventNavigation,
        },
      });
  }, [props.navigationManagement.isTryingToNavigate]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!draftCommitId) return;
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [draftCommitId]);

  useEffect(() => {
    if (!draftCommitId) return;
    getAccessToken();
    const tokenInterval = setInterval(() => {
      getAccessToken();
    }, fileAccessTokenRenewTime);

    return () => clearInterval(tokenInterval);
  }, [draftCommitId]);

  useEffect(() => {
    loadFiles(false);
  }, [props.topicId]);

  useEffect(() => {
    const root = wrapperRef.current;
    if (!root) return;

    const applyIds = () => {
      // Upload button
      const uploadBtn = root.querySelector<HTMLButtonElement>(
        ".fm-toolbar > div:first-child > button.item-action",
      );
      if (uploadBtn)
        uploadBtn.id = "topicDescriptionCreationFolderButtonUploadButton";

      // Delete button
      const deleteBtn = root.querySelector<HTMLButtonElement>(
        ".file-action-container > div:first-child > button.item-action",
      );
      if (deleteBtn)
        deleteBtn.id = "topicDescriptionCreationFolderButtonDeleteButton";

      // Choose file button
      const chooseFileBtn = root.querySelector<HTMLButtonElement>(
        ".fm-modal .btn-choose-file > button",
      );
      if (chooseFileBtn)
        chooseFileBtn.id =
          "topicDescriptionCreationFolderButtonChooseFileButton";

      // File items
      const fileItems = root.querySelectorAll<HTMLDivElement>(
        ".file-item-container",
      );
      fileItems.forEach((item) => {
        const fileName = item.getAttribute("title");
        if (fileName) {
          const safeName = fileName.replace(/[^a-zA-Z0-9_-]/g, "-");
          item.id = `topicDescriptionCreationFolderFileItem${safeName}`;
        }
      });
    };

    applyIds();

    const obs = new MutationObserver(applyIds);
    obs.observe(root, {childList: true, subtree: true});

    return () => obs.disconnect();
  }, [isEditing, files.length]);

  return (
    <>
      <h2>
        {
          <FormattedMessage id="topicDescriptionCreation.h2.multi_file_upload" />
        }
      </h2>
      <div className="topicDescriptionCreationFolderButtonContainer">
        {!isEditing && !props.disabledReason && (
          <Button
            label={intl.formatMessage({
              id: "topicDescriptionCreation.multi_file_upload.button.startEditing",
            })}
            idBase={"topicDescriptionCreationFolderButtonStartEditing"}
            onClick={startEditing}
            configuration={"secondary"}
          />
        )}
        {isEditing && (
          <Button
            isDisabled={!isEditing}
            label={intl.formatMessage({
              id: "topicDescriptionCreation.multi_file_upload.button.publishFolder",
            })}
            idBase={"topicDescriptionCreationFolderButtonPublishFolder"}
            onClick={publishDraftFolder}
            configuration={"primary"}
          />
        )}
      </div>
      <div
        ref={wrapperRef}
        className={`topicDescriptionCreationFolderWrapper ${isEditing ? "is-editing" : ""}`}
      >
        <FileManager
          layout="list"
          language={navigator.language.startsWith("de") ? "de-DE" : "en-US"}
          primaryColor="#ff3d00"
          key={isEditing + "-" + files.length}
          files={files}
          fileUploadConfig={fileUploadConfig}
          permissions={fileManagerPermissions}
          height={"100%"}
          defaultNavExpanded={false}
          enableFilePreview={false}
          onFileUploaded={handleFileUploaded}
          onDelete={handleFileDeleted}
        />
      </div>
    </>
  );
}

/**
 * Controls the transfer section when viewing topic descriptions with multi_file_upload transfer type.
 */
function FolderTransferSection(props: {
  alternative: "create" | "view" | "edit";
}) {
  if (props.alternative !== "create") return null;
  return (
    <>
      <div className="topicDescriptionCreationFolderInfo">
        <FormattedMessage id="topicDescriptionCreation.multi_file_upload.information" />
      </div>
    </>
  );
}

export {FolderSection, FolderTransferSection};
